import { FormattedDate, useIntl } from 'react-intl';
import { MasterBlock } from '../../../../../components/AccionaWidgets/MasterBlock';
import { Divider } from 'antd';
import AntdDatePicker from '../../../../../components/AntdDatePicker';
import dayjs from 'dayjs';
import styles from './index.module.scss';
import { useContext, useEffect, useState } from 'react';
import {
  getWorkEmissionsFirstDate,
  getWorkEmissionsUpdates,
} from '../../../../../services/api/workEmissions';
import { NotificationsContext } from '../../../../../context/NotificationsContext';
import { useOutletContext } from 'react-router-dom';
import AccionaButton from '../../../../../components/AccionaButton';
import useDownloadXlsxFile from './hooks/useDownLoadXlsxFile';

const { emissionsTable, container, headerContainer, datePicker } = styles;

const EmissionWidget = () => {
  const intl = useIntl();
  const { pushNotification } = useContext(NotificationsContext);
  const { project } = useOutletContext();
  const [firstDate, setFirstDate] = useState();
  const { loading: loadingRC, downloadXlsx: downloadRC } = useDownloadXlsxFile(
    project,
    'RC',
  );
  const { loading: loadingPC, downloadXlsx: downloadPC } = useDownloadXlsxFile(
    project,
    'PC',
  );
  const [data, setData] = useState();
  const [date, setDate] = useState({
    month: dayjs().month() + 1,
    year: dayjs().year(),
  });

  const resistantClasses = ['C20', 'C25', 'C30', 'C35', 'C40'];
  const cementTypes = [
    {
      key: 'CEMI',
      labelId: 'pages.quality.dashboards.emissionsWidget.cementType.cemI',
    },
    {
      key: 'CEMII',
      labelId: 'pages.quality.dashboards.emissionsWidget.cementType.cemII',
    },
    {
      key: 'CEMIII',
      labelId: 'pages.quality.dashboards.emissionsWidget.cementType.cemIII',
    },
    {
      key: 'CEMIV',
      labelId: 'pages.quality.dashboards.emissionsWidget.cementType.cemIV',
    },
    {
      key: 'CEMV',
      labelId: 'pages.quality.dashboards.emissionsWidget.cementType.cemV',
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getWorkEmissionsUpdates(
          date,
          project.id_project,
        );
        if (response.data) {
          setData(response.data);
        } else {
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.dashboards.emissionsWidget.error.noData',
            }),
            type: 'error',
          });
        }
      } catch (err) {
        console.error('Error fetching emissions data:', err);
        pushNotification({
          message: intl.formatMessage({
            id: 'pages.quality.dashboards.emissionsWidget.error',
          }),
          type: 'error',
        });
        setData(null);
      }
    };
    fetchData();
  }, [date, pushNotification, intl, project]);

  useEffect(() => {
    const fetchFirstDate = async () => {
      try {
        const firstDate = await getWorkEmissionsFirstDate(project.id_project);
        if (firstDate.data) setFirstDate(firstDate.data.date);
      } catch (error) {
        console.error('Error fetching first date:', error);
      }
    };
    fetchFirstDate();
  }, [project.id_project]);

  return (
    <MasterBlock
      title={intl.formatMessage({
        id: 'pages.quality.dashboards.emissionsWidget.title',
      })}
      color="#B0B0B0"
    >
      <div className={container}>
        <div className={headerContainer}>
          <h4 style={{ flex: 1 }}>
            {intl.formatMessage({
              id: 'pages.quality.dashboards.emissionsWidget.volume',
            })}
          </h4>
          <div style={{ display: 'flex', flex: 1, justifyContent: 'flex-end' }}>
            <AccionaButton
              id="downloadXlsxBtn"
              onClick={downloadRC}
              msg={intl.formatMessage({
                id: 'RC',
              })}
              classIcon="icon fas fa-download"
              type="primary"
              loading={loadingRC}
              style={{ height: 40 }}
            />
            <AccionaButton
              id="downloadXlsxBtn"
              onClick={downloadPC}
              msg={intl.formatMessage({
                id: 'PC',
              })}
              classIcon="icon fas fa-download"
              type="primary"
              loading={loadingPC}
              style={{ height: 40 }}
            />
            <AntdDatePicker
              picker="month"
              format="MMMM YYYY"
              onChange={(date) => {
                setDate({ month: date.month() + 1, year: date.year() });
              }}
              disabledDate={(current) => {
                const min = dayjs(firstDate).startOf('month');
                const max = dayjs().endOf('month');
                if (!current) return false;
                return (
                  current.isBefore(min, 'day') || current.isAfter(max, 'day')
                );
              }}
              className={datePicker}
              value={dayjs(
                `${date.year}-${date.month.toString().padStart(2, '0')}`,
                'YYYY-MM',
              )}
              style={{ marginLeft: 5, flex: 1 }}
            />
          </div>
        </div>
        <Divider />
        <h4>
          <FormattedDate
            value={dayjs(
              `${date.year}-${date.month.toString().padStart(2, '0')}`,
              'YYYY-MM',
            )}
            year="numeric"
            month="long"
          />
        </h4>

        <table width="100%" className={emissionsTable}>
          <thead>
            <tr>
              <th>
                {intl.formatMessage({
                  id: 'pages.quality.dashboards.emissionsWidget.cementType',
                })}
              </th>
              {resistantClasses.map((resistantClass) => (
                <th key={resistantClass}>
                  {intl.formatMessage({
                    id: `pages.quality.dashboards.emissionsWidget.resistantClass${resistantClass.slice(
                      1,
                    )}`,
                  })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cementTypes.map((cementType) => (
              <tr key={cementType.key}>
                <td>
                  {intl.formatMessage({
                    id: cementType.labelId,
                  })}
                </td>
                {resistantClasses.map((resistantClass) => (
                  <td key={`${cementType.key}-${resistantClass}`}>
                    {data?.[cementType.key]?.[resistantClass] ?? '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MasterBlock>
  );
};

export default EmissionWidget;
